import type { ReactNode } from 'react';
import { useTranslate } from '@refinedev/core';
import { Alert, Card, Flex, Tag, Typography, theme } from 'antd';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import type { PublisherCoverageCheck } from '@/components/tenancy/publisherStanding';
import type { PublisherStandingResult } from '@/hooks/tenancy/usePublisherStanding';
import { formatDate } from '@/utils/format';

const { Text } = Typography;

interface PublisherStandingCardProps {
  result: PublisherStandingResult;
}

/**
 * Where the publisher stands for the enable rule: its organization today,
 * the moves already scheduled, and the contract covering each of those
 * days. Read-only — links and contracts are managed in the organization.
 */
export const PublisherStandingCard = ({
  result: { standing, organizationName, isLoading, isError },
}: PublisherStandingCardProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const k = (key: string, opts?: Record<string, unknown>) =>
    t(`user_catalog.publishers.standing.${key}`, opts);

  const organization = (id: number) => (
    <ShowIconLink resource="tenancy.organizations" id={id}>
      {organizationName(id) ?? `#${id}`}
    </ShowIconLink>
  );

  const section = (label: string, children: ReactNode) => (
    <div>
      <Text
        type="secondary"
        style={{ display: 'block', fontSize: token.fontSizeSM }}
      >
        {label}
      </Text>
      {children}
    </div>
  );

  const coverage = (check: PublisherCoverageCheck, isToday: boolean) => (
    <Flex key={check.day} gap={token.marginXS} align="baseline" wrap>
      <Text>
        {isToday ? k('today') : k('from', { date: formatDate(check.day) })}
      </Text>
      <Text type="secondary">·</Text>
      {check.contract ? (
        <>
          <ShowIconLink resource="tenancy.contracts" id={check.contract.id}>
            {k('contract', { id: check.contract.id })}
          </ShowIconLink>
          <Tag>
            {check.contract.publisher_id === null
              ? k('contract_organization')
              : k('contract_publisher')}
          </Tag>
        </>
      ) : (
        <Text type="danger">
          {k('uncovered', {
            organization:
              organizationName(check.link.organization_id) ??
              `#${check.link.organization_id}`,
          })}
        </Text>
      )}
    </Flex>
  );

  const canEnable = standing.violations.length === 0;

  return (
    <Card
      title={k('title')}
      loading={isLoading}
      extra={
        !isError && (
          <Tag color={canEnable ? 'success' : 'error'}>
            {canEnable ? k('can_enable') : k('cannot_enable')}
          </Tag>
        )
      }
    >
      {isError ? (
        <Alert type="error" showIcon message={k('load_error')} />
      ) : (
        <Flex vertical gap={token.marginSM}>
          {section(
            k('organization_today'),
            standing.current ? (
              organization(standing.current.organization_id)
            ) : (
              <Text type="danger">{k('no_organization')}</Text>
            )
          )}
          {standing.scheduled.length > 0 &&
            section(
              k('scheduled'),
              <Flex vertical gap={token.marginXXS}>
                {standing.scheduled.map((link) => (
                  <Flex key={link.id} gap={token.marginXS} align="baseline">
                    <Text>
                      {k('from', { date: formatDate(link.valid_from) })}
                    </Text>
                    <Text type="secondary">→</Text>
                    {organization(link.organization_id)}
                  </Flex>
                ))}
              </Flex>
            )}
          {standing.checks.length > 0 &&
            section(
              k('coverage'),
              <Flex vertical gap={token.marginXXS}>
                {standing.checks.map((check, i) =>
                  coverage(check, i === 0 && standing.current != null)
                )}
              </Flex>
            )}
          {!canEnable && (
            <Flex vertical gap={token.marginXXS}>
              {standing.violations.map((violation) => (
                <Text key={violation} type="danger">
                  {k(`violations.${violation}`)}
                </Text>
              ))}
            </Flex>
          )}
        </Flex>
      )}
    </Card>
  );
};
